import { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, FormProvider } from 'react-hook-form';
import { v4 as newUuid } from 'uuid';
import {
  postIntakeFormEmailRequest,
  postBatchIntakeFormEmailRequest,
  resetClientEmailState,
  selectClientEmailState,
  selectSessionAdvisor,
} from '@client/model';
import { Tag } from '@client/components';
import { getIntroIntakeFormApiEndPoint } from '@shared';

interface BatchInviteFormData {
  advisorProvidedBody: string;
  sendClientResultsLink: boolean;
  clientRelationshipStage?: ClientRelationshipStage;
  selectedReferralSourceId?: string;
  clientFirstName?: string;
  clientLastName?: string;
}

interface EmailData {
  email: string;
  intakeFormLink: string;
  surveyId: string;
  advisorId: string;
  clientRelationshipStage?: ClientRelationshipStage;
  advisorProvidedBody?: string;
  referralSourceId?: string;
  advisorFirstName?: string;
  advisorOrganization?: string;
}

export function useInviteClientModal({
  open,
  defaultTab,
  shouldPrepopulateEmail,
  maxEmails,
  onClose,
}: {
  open: boolean;
  defaultTab: string;
  shouldPrepopulateEmail?: boolean;
  maxEmails?: number;
  onClose: () => void;
}) {
  const dispatch = useDispatch();
  const advisor = useSelector(selectSessionAdvisor);
  const {
    status: clientEmailStatus,
    errorMessage,
    failedEmails,
  } = useSelector(selectClientEmailState);

  const statusBulk = clientEmailStatus.batchIntakeForm;
  const statusSingle = clientEmailStatus.individualIntakeForm;

  const [emails, setEmails] = useState<Tag[]>([]);
  const [complianceConfirmed, setComplianceConfirmed] = useState(false);
  const [selectedTab, setSelectedTab] = useState<string>(defaultTab);
  const [showPreview, setShowPreview] = useState(false);
  const emailDataRef = useRef<EmailData[]>([]);

  const methods = useForm<BatchInviteFormData>({
    defaultValues: {
      advisorProvidedBody: '',
      sendClientResultsLink: advisor.sendClientResultsLinkSetting ?? false,
      clientRelationshipStage: undefined,
      selectedReferralSourceId: undefined,
      clientFirstName: '',
      clientLastName: '',
    },
    mode: 'onChange',
  });

  const { watch, setValue, reset, clearErrors } = methods;
  const formData = watch();

  const validEmails = emails.filter(e => !e.error).map(e => e.value.trim());
  const uniqueValidEmails = Array.from(new Set(validEmails.map(v => v.toLowerCase()))).map(
    v => validEmails.find(orig => orig.toLowerCase() === v) || v,
  );

  const isSingleInvite = uniqueValidEmails.length === 1;
  const isBulkInvite = uniqueValidEmails.length > 1;

  const isLoading = statusBulk === 'loading' || statusSingle === 'loading';
  const isComplete = statusBulk === 'complete' || statusSingle === 'complete';
  const isError =
    statusBulk === 'error' || statusSingle === 'error' || statusBulk === 'completeWithErrors';

  // Reset state
  const resetState = () => {
    setEmails([]);
    dispatch(resetClientEmailState());
    reset({
      advisorProvidedBody: '',
      sendClientResultsLink: advisor.sendClientResultsLinkSetting ?? false,
      clientRelationshipStage: undefined,
      selectedReferralSourceId: undefined,
      clientFirstName: '',
      clientLastName: '',
    });
    emailDataRef.current = [];
    setComplianceConfirmed(false);
    clearErrors();
  };

  useEffect(() => {
    if (open) {
      setSelectedTab(defaultTab);
      resetState();

      if (shouldPrepopulateEmail && advisor?.advisorEmail) {
        setEmails([{ value: advisor.advisorEmail, isValid: true, error: '' }]);
      }
    }
  }, [open]);

  useEffect(() => {
    setSelectedTab(defaultTab);
  }, [defaultTab]);

  useEffect(() => {
    if (statusBulk === 'completeWithErrors' && failedEmails?.length) {
      setEmails(current => current.filter(t => failedEmails.includes(t.value)));
      emailDataRef.current = emailDataRef.current.filter(d => failedEmails.includes(d.email));
    }
  }, [statusBulk, failedEmails]);

  // Bulk payload builder
  const buildBulkPayload = (emailsList: string[], fd: BatchInviteFormData): EmailData[] => {
    return emailsList.map(email => {
      const surveyId = newUuid();
      return {
        email,
        surveyId,
        advisorId: advisor.advisorId,
        advisorFirstName: advisor.advisorFirstName,
        advisorOrganization: advisor.organizationDisplayName,
        intakeFormLink: getIntroIntakeFormApiEndPoint(
          advisor.advisorId,
          fd.sendClientResultsLink,
          surveyId,
        ),
        clientRelationshipStage: fd.clientRelationshipStage,
        referralSourceId: fd.selectedReferralSourceId,
        advisorProvidedBody: fd.advisorProvidedBody,
      };
    });
  };

  // Submit handler
  const handleSubmit = async () => {
    if (uniqueValidEmails.length === 0) return;

    // Single invite
    if (isSingleInvite) {
      const surveyId = newUuid();
      dispatch(
        postIntakeFormEmailRequest({
          advisorId: advisor.advisorId,
          email: uniqueValidEmails[0],
          clientFirstName: (formData.clientFirstName || '').trim(),
          clientLastName: (formData.clientLastName || '').trim(),
          advisorProvidedBody: formData.advisorProvidedBody,
          intakeFormLink: getIntroIntakeFormApiEndPoint(
            advisor.advisorId,
            formData.sendClientResultsLink,
            surveyId,
          ),
          surveyId,
          createNewClient: true,
          clientRelationshipStage: formData.clientRelationshipStage,
          referralSourceId: formData.selectedReferralSourceId,
          isTestEmail: false,
        }),
      );
      return;
    }

    // Bulk invite
    if (!complianceConfirmed) return;

    const payload = buildBulkPayload(uniqueValidEmails, formData);
    emailDataRef.current = payload;
    dispatch(postBatchIntakeFormEmailRequest({ intakeFormEmailProps: payload }));
  };

  const handleSendTestEmail = async () => {
    dispatch(
      postIntakeFormEmailRequest({
        advisorId: advisor.advisorId,
        email: advisor.advisorEmail,
        intakeFormLink: getIntroIntakeFormApiEndPoint(
          advisor.advisorId,
          formData.sendClientResultsLink,
          newUuid(),
        ),
        isTestEmail: true,
        advisorProvidedBody: formData.advisorProvidedBody,
      }),
    );
  };

  const handleClose = () => {
    onClose();
    resetState();
    dispatch({ type: 'RESUME_SESSION_REFRESH' });
  };

  useEffect(() => {
    clearErrors(['clientFirstName', 'clientLastName']);
  }, [selectedTab]);

  return {
    advisor,
    emails,
    setEmails,
    complianceConfirmed,
    setComplianceConfirmed,
    selectedTab,
    setSelectedTab,
    showPreview,
    setShowPreview,
    isSingleInvite,
    isBulkInvite,
    uniqueValidEmails,
    isLoading,
    isComplete,
    isError,
    errorMessage,
    handleSubmit,
    handleSendTestEmail,
    handleClose,
    methods,
    resetState,
    setValue,
    formData,
  };
}
